import * as React from "react";
import * as d3 from "d3";
import { BaseType } from "d3";
import * as cn from "classnames/bind";
import { drawCoverPreview } from "svg/draw/draw-cover-preview";
import {
    CalendarState,
    TextInformation,
    Placement,
    CustomImage,
    ImageDetails,
    RubberBandItem,
    SpiralItem,
    CalendarDrawerContent,
    CalendarPreviewDrawerSettings,
    BurdeFontResponse,
} from "burde";
import { actionCreators } from "page/calendar/store";
import { actionCreators as uiActionCreators } from "ui/store";
import * as styles from "./cover.css";
import { NavigationComponent } from "ui/constants";
import { getTransformation } from "component/page/calendar/step/2-cover/positioning-helper";
import { CalendarSizes } from "svg/constants";

const cx = cn.bind(styles);

type CalendarCoverProps = {
    calendar: CalendarState;
    activeComponent: NavigationComponent;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    spiral: SpiralItem | null;
    rubberband: RubberBandItem | null;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    fontOptions: BurdeFontResponse | null;
} & typeof actionCreators &
    typeof uiActionCreators;

class CalendarCover extends React.Component<CalendarCoverProps> {
    private _divRef?: HTMLDivElement | null;

    updateText(placement: Placement, index: number) {
        const texts = [...this.props.calendar.coverTexts];
        texts[index].x = placement.X;
        texts[index].y = placement.Y;
        this.props.updateCoverTextCollection(texts);
    }

    private drawCover = (props: CalendarCoverProps, withAnimation: boolean) => {
        if (!this._divRef) {
            return;
        }

        const { calendar, activeComponent, fontOptions } = props;

        let deltaX: number, deltaY: number, rotate: number;
        let updater = this.updateText.bind(this);

        var dragHandler = d3
            .drag()
            .on("start", function () {
                var current = d3.select(this);

                var transform = getTransformation(current.attr("transform"));

                deltaX = transform.translateX - d3.event.x;
                deltaY = transform.translateY - d3.event.y;
                rotate = transform.rotate;
            })
            .on("drag", function () {
                d3.select(this).attr(
                    "transform",
                    `translate(${d3.event.x + deltaX} ${
                        d3.event.y + deltaY
                    }) rotate(${rotate})`,
                );
            })
            .on("end", function () {
                var daText = d3.select(this);

                const index = daText.attr("data-index");

                if (index && calendar.coverTexts[Number(index)]) {
                    var transform = getTransformation(daText.attr("transform"));

                    var placement: Placement = {
                        X: Number(transform.translateX),
                        Y: Number(transform.translateY),
                    };

                    updater(placement, Number(index));
                }
            });

        const calendarPreviewDrawerSettings: CalendarPreviewDrawerSettings = {
            isFrontPage: activeComponent.name !== "backpage",
            withAnimation: withAnimation,
            withPlasticCover:
                calendar.calendarSize.toLowerCase() !== CalendarSizes.B5, //NOTE: Refactor this to be more dynamic in the future
            showCalendarName: true,
        };

        const calendarDrawerContent: CalendarDrawerContent = {
            article: null,
            calendarId: null,
            layout: null,
            dayModules: null,
            weekModules: null,
            metadata: [],
            coverImage: this.props.coverImage,
            backCoverImage: this.props.backCoverImage,
            customCoverImage: this.props.customCoverImage,
            customBackImage: this.props.customBackImage,
            borderImage: null,
            extraPages: null,
            salesPages: null,
            customerInformation: null,
            ownerPage: null,
            monthDivider: null,
            ownerPageImageUrl: "",
            weekNumberPrefix: "",
            spiral: this.props.spiral,
            rubberband: this.props.rubberband,
            calendarName: calendar.name,
            fonts: fontOptions,
        };

        drawCoverPreview(
            calendar,
            calendarPreviewDrawerSettings,
            calendarDrawerContent,
            this._divRef,
            () => {
                if (
                    activeComponent.name === "coverText" &&
                    this._divRef != null
                ) {
                    let texts = d3.select(this._divRef).selectAll("text");
                    texts
                        .on("mouseover", function (d) {
                            d3.select(this).style("cursor", "move");
                        })
                        .on("mouseout", function (d) {
                            d3.select(this).style("cursor", "default");
                        });
                    dragHandler(texts as any);
                }
            },
        );
    };

    public componentDidUpdate(prevProps: CalendarCoverProps) {
        const withAnimation =
            (this.props.activeComponent.name === "backpage" ||
                prevProps.activeComponent.name === "backpage") &&
            this.props.activeComponent.name !== prevProps.activeComponent.name;

        if (withAnimation) {
            d3.select(this._divRef as BaseType)
                .transition()
                .duration(200)
                .delay(0)
                .style("opacity", "0")
                .style("transform", "scale(0.5)")
                .on("end", () => {
                    this.drawCover(this.props, false);
                })
                .transition()
                .duration(200)
                .delay(0)
                .style("opacity", "1")
                .style("transform", "scale(1)");
        } else {
            this.drawCover(this.props, false);
        }
    }

    public componentDidMount() {
        this.drawCover(this.props, false);
    }

    public render() {
        const svgContainerCn = () =>
            cx({
                svgContainer: true,
                "svg-container": true,
            });

        const calendarContainerCn = () =>
            cx({
                calendarContainer: true,
            });

        return (
            <div key={"calendar-content"} className={calendarContainerCn()}>
                <div
                    className={svgContainerCn()}
                    ref={(divRef) => (this._divRef = divRef)}
                />
            </div>
        );
    }
}

export default CalendarCover;
